import React from "react";
import { AccountTreeOutlined, Check, HistoryOutlined, InboxOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Skeleton, Typography } from "@mui/material";
import { WORKFLOW_CANVAS_LABELS, WorkflowCanvas } from "./workflowBuilderData";
import {
  formatHistoryTime,
  HISTORY_SYSTEM_ACTOR,
  WorkflowRunHistoryEntry,
} from "./workflowRunHistory";

export type WorkflowPipelineStepState = "done" | "current" | "waiting" | "rejected" | "skipped";

const STEP_STATE_LABELS: Record<WorkflowPipelineStepState, string> = {
  done: "Done",
  current: "Current",
  waiting: "Waiting",
  rejected: "Rejected",
  skipped: "Skipped",
};

const SKELETON_STEPS = 4;

interface WorkflowRunRailProps {
  /** Renders skeleton rows while the case is still loading. */
  loading?: boolean;
  reference?: string;
  definitionLabel?: string;
  statusLabel?: string;
  /** BEM modifier for the status chip — "active", "completed", "rejected" or "cancelled". */
  statusModifier?: string;
  stages?: WorkflowCanvas[];
  /** One entry per stage, same order as `stages`. */
  stepStates?: WorkflowPipelineStepState[];
  progressLabel?: string;
  /** A real case's steps so far, oldest first. Omitted (no card) when there is no case yet. */
  history?: WorkflowRunHistoryEntry[];
}

/** The sticky right rail of the Test / Run screen: the Reference card and the Instance pipeline. */
const WorkflowRunRail: React.FC<WorkflowRunRailProps> = ({
  loading = false,
  reference = "",
  definitionLabel = "",
  statusLabel = "",
  statusModifier = "active",
  stages = [],
  stepStates = [],
  progressLabel = "",
  history,
}) => (
  <Box component="aside" className="workflow-run__rail" aria-label="Instance details" aria-busy={loading}>
    <Card className="workflow-run__rail-card" variant="outlined">
      <CardContent>
        <Typography className="workflow-run__rail-title">
          <InboxOutlined aria-hidden="true" />
          Reference
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="text" width="70%" height={28} />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="rounded" width={88} height={22} />
          </>
        ) : (
          <>
            <Typography className="workflow-run__reference">{reference}</Typography>
            <Typography className="workflow-run__definition">{definitionLabel}</Typography>
            <Chip
              className={`workflow-run__status workflow-run__status--${statusModifier}`}
              label={
                <>
                  <span className="workflow-run__status-dot" aria-hidden="true" />
                  {statusLabel}
                </>
              }
              size="small"
            />
          </>
        )}
      </CardContent>
    </Card>

    <Card className="workflow-run__rail-card" variant="outlined">
      <CardContent>
        <Typography className="workflow-run__rail-title">
          <AccountTreeOutlined aria-hidden="true" />
          Instance pipeline{loading ? "" : ` ${progressLabel}`}
        </Typography>
        {loading ? (
          Array.from({ length: SKELETON_STEPS }, (_, index) => (
            <Box key={index} className="workflow-run__pipeline-step">
              <Skeleton variant="circular" width={24} height={24} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="75%" />
                <Skeleton variant="text" width="45%" />
              </Box>
            </Box>
          ))
        ) : (
          <Box component="ol" className="workflow-run__pipeline" aria-label="Instance pipeline">
            {stages.map((stage, index) => {
              const stepState = stepStates[index] ?? "waiting";
              return (
                <Box
                  component="li"
                  key={stage.id}
                  className={`workflow-run__pipeline-step workflow-run__pipeline-step--${stepState}`}
                >
                  <span className="workflow-run__step-marker">
                    {stepState === "done" ? <Check /> : index + 1}
                  </span>
                  <span className="workflow-run__step-copy">
                    <strong>{stage.name}</strong>
                    <span>
                      {WORKFLOW_CANVAS_LABELS[stage.type]} · {STEP_STATE_LABELS[stepState]}
                    </span>
                  </span>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>

    {!loading && history && (
      <Card className="workflow-run__rail-card" variant="outlined">
        <CardContent>
          <Typography className="workflow-run__rail-title">
            <HistoryOutlined aria-hidden="true" />
            History
          </Typography>
          {history.length === 0 ? (
            <Typography className="workflow-run__history-empty">Nothing has happened on this case yet.</Typography>
          ) : (
            <Box component="ol" className="workflow-run__history" aria-label="Case history">
              {history.map((entry) => (
                <Box component="li" key={entry.key} className="workflow-run__history-entry">
                  <strong>{entry.action}</strong>
                  {entry.step && <span className="workflow-run__history-step">{entry.step}</span>}
                  <span className="workflow-run__history-meta">
                    {entry.person ?? HISTORY_SYSTEM_ACTOR}
                    {entry.intendedFor && ` · for ${entry.intendedFor}`}
                  </span>
                  {entry.at && <span className="workflow-run__history-meta">{formatHistoryTime(entry.at)}</span>}
                  {entry.reason && <span className="workflow-run__history-reason">{entry.reason}</span>}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    )}
  </Box>
);

export default WorkflowRunRail;
